import { formatCurrency } from '@crm/shared';
import { Button, cn, Input, Label, Spinner } from '@crm/ui';
import { Check, Phone, Search } from 'lucide-react';
import { useState } from 'react';
import { EmptyState } from '@/components';
import { toErrorMessage } from '@/modules/auth/utils';
import { type AvailableNumber, useAvailableNumbers, useContractLine } from '../api';

function NumberRow({
  numero,
  escolhido,
  onPick,
}: {
  numero: AvailableNumber;
  escolhido: boolean;
  onPick: () => void;
}) {
  return (
    <li>
      <button
        type="button"
        onClick={onPick}
        className={cn(
          'flex w-full items-center gap-3 rounded-lg border p-3 text-left transition-colors',
          escolhido ? 'border-primary bg-primary/10' : 'border-border hover:bg-muted/60',
        )}
      >
        <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary/15 text-primary">
          <Phone className="h-4 w-4" />
        </span>

        <span className="min-w-0 flex-1">
          <span className="block truncate font-medium">{numero.friendlyName}</span>
          <span className="block truncate text-xs text-muted-foreground">
            {[numero.locality, numero.region].filter(Boolean).join(' - ') || numero.phoneNumber}
          </span>
        </span>

        <span className="shrink-0 text-right text-sm">
          <span className="block font-medium">{formatCurrency(numero.monthlyPrice)}</span>
          <span className="block text-xs text-muted-foreground">por mes</span>
        </span>

        {escolhido ? <Check className="h-4 w-4 shrink-0 text-primary" /> : null}
      </button>
    </li>
  );
}

export function NumberPicker({ unitId }: { unitId: string }) {
  const [ddd, setDdd] = useState('');
  const [buscou, setBuscou] = useState(false);
  const [escolhido, setEscolhido] = useState<string | null>(null);

  const disponiveis = useAvailableNumbers({ areaCode: ddd, enabled: buscou });
  const contratar = useContractLine();

  const lista = disponiveis.data?.data ?? [];

  function procurar(event: React.FormEvent) {
    event.preventDefault();
    setBuscou(true);
    void disponiveis.refetch();
  }

  return (
    <div className="space-y-5">
      <form onSubmit={procurar} className="flex flex-wrap items-end gap-3">
        <div className="space-y-2">
          <Label htmlFor="linha-ddd" className="text-xs font-normal">
            DDD
          </Label>
          <Input
            id="linha-ddd"
            inputMode="numeric"
            placeholder="11"
            className="w-24"
            value={ddd}
            maxLength={3}
            onChange={(event) => setDdd(event.target.value.replace(/\D/g, ''))}
          />
        </div>

        <Button type="submit" variant="outline" disabled={disponiveis.isFetching}>
          {disponiveis.isFetching ? <Spinner /> : <Search className="h-4 w-4" />}
          Procurar numeros
        </Button>
      </form>

      {disponiveis.error ? (
        <p className="text-sm text-destructive">{toErrorMessage(disponiveis.error)}</p>
      ) : null}

      {buscou && !disponiveis.isFetching ? (
        lista.length === 0 ? (
          <EmptyState icon={<Phone />} title="Nenhum numero disponivel neste DDD" />
        ) : (
          <ul className="space-y-2">
            {lista.map((numero) => (
              <NumberRow
                key={numero.phoneNumber}
                numero={numero}
                escolhido={numero.phoneNumber === escolhido}
                onPick={() => setEscolhido(numero.phoneNumber)}
              />
            ))}
          </ul>
        )
      ) : null}

      {escolhido ? (
        <div className="space-y-3 border-t border-border pt-4">
          <Button
            type="button"
            disabled={contratar.isPending}
            onClick={() => contratar.mutate({ phoneNumber: escolhido, unitId })}
          >
            {contratar.isPending ? <Spinner /> : <Check className="h-4 w-4" />}
            Reservar este numero
          </Button>

          <p className="text-xs text-muted-foreground">
            A reserva nao gera cobranca. O numero e comprado apos o pagamento do primeiro mes.
          </p>

          {contratar.error ? (
            <p className="text-sm text-destructive">{toErrorMessage(contratar.error)}</p>
          ) : null}
        </div>
      ) : null}
    </div>
  );
}
